<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.TRANSACTIONS.DETAIL.SHOW"
    :handler="() => $refs.modal.show()"
  />

  <DialogComponent ref="modal" type="modal" noInnerPadding hideCloseButton>
    <div
      class="w-full h-full flex flex-col overflow-auto p-4 gap-4 bg-slate-50"
      :class="{ shimmerContainer: loading }"
    >
      <div class="mobile-status-padding"></div>

      <!-- Header with actions -->
      <div class="flex items-center justify-between">
        <button
          class="w-10 h-10 p-2 rounded-full bg-slate-200"
          @click="() => $refs.modal.hide()"
        >
          <ColorImg
            class="w-full h-full bg-primary -rotate-90"
            src="/images/all/arrow-up.svg"
          />
        </button>

        <div class="flex gap-2">
          <!-- Retry destination button (only if srcStatus is success) -->
          <button
            v-if="canRetryDestination"
            class="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium"
            @click="retryDestination"
          >
            Relancer destination
          </button>

          <!-- Mark as refunded button (always available) -->
          <button
            class="px-4 py-2 rounded-lg bg-purple-500 text-white text-sm font-medium"
            @click="markAsRefunded"
          >
            Rembourser
          </button>

          <!-- Mark as success button (always available) -->
          <button
            class="px-4 py-2 rounded-lg bg-green-500 text-white text-sm font-medium"
            @click="markAsSuccess"
          >
            Marquer réussi
          </button>
        </div>
      </div>

      <!-- Transaction reference card -->
      <div class="bg-white rounded-xl p-4 flex items-center gap-4">
        <ImgComponent
          :src="transaction?.logo"
          class="w-12 h-12 rounded-lg object-contain"
        />
        <div class="flex-1">
          <div class="text-sm text-gray-500">
            {{ transaction?.operationNameDisplay || transaction?.operation }}
          </div>
          <div class="text-lg font-semibold text-gray-900">
            #{{ transaction?.reference || transaction?.id }}
          </div>
        </div>
        <div class="text-right">
          <div class="text-xs text-gray-500">Statut</div>
          <div
            class="text-sm font-medium px-3 py-1 rounded-full"
            :class="getStatusClass(transaction?.status)"
          >
            {{ getStatusText(transaction?.status) }}
          </div>
        </div>
      </div>

      <!-- Amounts card -->
      <div class="grid grid-cols-2 gap-4">
        <div class="bg-white rounded-xl p-4">
          <div class="text-sm text-gray-500 mb-1">Envoyé</div>
          <div class="text-xl font-bold text-gray-900">
            {{ formatNumber(transaction?.amountToSend) }}
            {{ transaction?.currencyToSend }}
          </div>
        </div>
        <div class="bg-white rounded-xl p-4">
          <div class="text-sm text-gray-500 mb-1">Reçu</div>
          <div class="text-xl font-bold text-green-600">
            {{ formatNumber(transaction?.amountToReceive) }}
            {{ transaction?.currencyToReceive }}
          </div>
        </div>
      </div>

      <!-- Fee -->
      <div class="bg-white rounded-xl p-4">
        <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">Frais totaux</span>
          <span class="font-bold text-gray-900">
            {{ formatNumber(transaction?.fee) }} XOF
          </span>
        </div>
      </div>

      <!-- Status details -->
      <div class="bg-white rounded-xl p-4">
        <h3 class="font-semibold text-gray-900 mb-4">Statuts</h3>
        <div class="space-y-3">
          <div class="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <div>
              <div class="text-xs text-gray-500">Source (collecte)</div>
              <div class="font-medium text-gray-900">
                {{ transaction?.collectStatusDisplay || 'Inconnu' }}
              </div>
            </div>
            <div
              class="w-3 h-3 rounded-full"
              :class="getStatusDotClass(transaction?.collectStatus)"
            ></div>
          </div>
          <div class="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <div>
              <div class="text-xs text-gray-500">Destination (dépôt)</div>
              <div class="font-medium text-gray-900">
                {{ transaction?.depositStatusDisplay || 'Inconnu' }}
              </div>
            </div>
            <div
              class="w-3 h-3 rounded-full"
              :class="getStatusDotClass(transaction?.depositStatus)"
            ></div>
          </div>
        </div>
      </div>

      <!-- User info -->
      <div class="bg-white rounded-xl p-4">
        <h3 class="font-semibold text-gray-900 mb-4">Utilisateur</h3>
        <div class="space-y-2">
          <div v-if="transaction?.senderFirstName || transaction?.senderLastName">
            <span class="text-xs text-gray-500">Nom</span>
            <div class="text-sm font-medium text-gray-900">
              {{ transaction?.senderFirstName }} {{ transaction?.senderLastName }}
            </div>
          </div>
          <div v-if="transaction?.senderPhoneNumber">
            <span class="text-xs text-gray-500">Téléphone</span>
            <div class="text-sm text-gray-900">{{ transaction?.senderPhoneNumber }}</div>
          </div>
          <div v-if="transaction?.senderEmail">
            <span class="text-xs text-gray-500">Email</span>
            <div class="text-sm text-gray-900">{{ transaction?.senderEmail }}</div>
          </div>
          <div v-if="transaction?.senderCountryCode">
            <span class="text-xs text-gray-500">Pays</span>
            <div class="text-sm text-gray-900">{{ transaction?.senderCountryCode }}</div>
          </div>
        </div>
      </div>

      <!-- Operation info -->
      <div class="bg-white rounded-xl p-4">
        <h3 class="font-semibold text-gray-900 mb-4">Opération</h3>
        <div class="space-y-2">
          <div>
            <span class="text-xs text-gray-500">Service</span>
            <div class="text-sm font-medium text-gray-900">
              {{ transaction?.serviceName || transaction?.service }}
            </div>
          </div>
          <div>
            <span class="text-xs text-gray-500">Type</span>
            <div class="text-sm text-gray-900">{{ transaction?.operation }}</div>
          </div>
          <div v-if="transaction?.bookmaker">
            <span class="text-xs text-gray-500">Bookmaker</span>
            <div class="text-sm text-gray-900">{{ transaction?.bookmaker }}</div>
          </div>
          <div v-if="transaction?.bookmakerId">
            <span class="text-xs text-gray-500">ID Bookmaker</span>
            <div class="text-sm text-gray-900">{{ transaction?.bookmakerId }}</div>
          </div>
        </div>
      </div>

      <!-- Recipient info -->
      <div v-if="transaction?.recipientPhoneNumber" class="bg-white rounded-xl p-4">
        <h3 class="font-semibold text-gray-900 mb-4">Bénéficiaire</h3>
        <div class="space-y-2">
          <div>
            <span class="text-xs text-gray-500">Téléphone</span>
            <div class="text-sm text-gray-900">
              {{ transaction?.recipientPhoneNumber }}
            </div>
          </div>
        </div>
      </div>

      <!-- Fee breakdown -->
      <div v-if="transaction?.feeBreakdown" class="bg-white rounded-xl p-4">
        <h3 class="font-semibold text-gray-900 mb-4">Détail des frais</h3>
        <div class="grid grid-cols-2 gap-3">
          <div
            v-if="transaction?.feeBreakdown?.srcServiceFee !== undefined"
            class="bg-gray-50 rounded-lg p-3"
          >
            <div class="text-xs text-gray-600">Service source</div>
            <div class="text-sm font-semibold text-gray-900">
              {{ formatNumber(transaction?.feeBreakdown?.srcServiceFee) }} XOF
            </div>
          </div>
          <div
            v-if="transaction?.feeBreakdown?.dstServiceFee !== undefined"
            class="bg-gray-50 rounded-lg p-3"
          >
            <div class="text-xs text-gray-600">Service dest.</div>
            <div class="text-sm font-semibold text-gray-900">
              {{ formatNumber(transaction?.feeBreakdown?.dstServiceFee) }} XOF
            </div>
          </div>
          <div
            v-if="transaction?.feeBreakdown?.srcProcessorFee !== undefined"
            class="bg-gray-50 rounded-lg p-3"
          >
            <div class="text-xs text-gray-600">Processeur source</div>
            <div class="text-sm font-semibold text-gray-900">
              {{ formatNumber(transaction?.feeBreakdown?.srcProcessorFee) }} XOF
            </div>
          </div>
          <div
            v-if="transaction?.feeBreakdown?.dstProcessorFee !== undefined"
            class="bg-gray-50 rounded-lg p-3"
          >
            <div class="text-xs text-gray-600">Processeur dest.</div>
            <div class="text-sm font-semibold text-gray-900">
              {{ formatNumber(transaction?.feeBreakdown?.dstProcessorFee) }} XOF
            </div>
          </div>
          <div
            v-if="transaction?.feeBreakdown?.earning !== undefined"
            class="bg-green-50 rounded-lg p-3"
          >
            <div class="text-xs text-gray-600">Gain</div>
            <div class="text-sm font-semibold text-green-600">
              {{ formatNumber(transaction?.feeBreakdown?.earning) }} XOF
            </div>
          </div>
          <div
            v-if="transaction?.feeBreakdown?.bonus !== undefined"
            class="bg-purple-50 rounded-lg p-3"
          >
            <div class="text-xs text-gray-600">Bonus</div>
            <div class="text-sm font-semibold text-purple-600">
              {{ formatNumber(transaction?.feeBreakdown?.bonus) }} XOF
            </div>
          </div>
        </div>
      </div>

      <!-- Timeline -->
      <div class="bg-white rounded-xl p-4">
        <h3 class="font-semibold text-gray-900 mb-4">Chronologie</h3>
        <div class="space-y-3">
          <div class="flex items-start gap-3">
            <div class="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
            <div class="flex-1">
              <div class="text-sm font-medium text-gray-900">Création</div>
              <div class="text-xs text-gray-500">{{ transaction?.startDate }}</div>
            </div>
          </div>
          <div
            v-if="
              transaction?.createdAt &&
              transaction?.updatedAt !== transaction?.createdAt
            "
            class="flex items-start gap-3"
          >
            <div class="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
            <div class="flex-1">
              <div class="text-sm font-medium text-gray-900">Dernière mise à jour</div>
              <div class="text-xs text-gray-500">
                {{ formatDate(transaction?.updatedAt) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Raw data (debug) -->
      <details class="bg-white rounded-xl p-4">
        <summary class="text-sm font-semibold text-gray-900 cursor-pointer">
          Données brutes
        </summary>
        <pre class="mt-3 text-xs bg-gray-50 p-3 rounded-lg overflow-auto">{{
          JSON.stringify(transaction, null, 2)
        }}</pre>
      </details>
    </div>
  </DialogComponent>
</template>

<script>
import Commons from '../../shared/libraries/commons';

export default {
  name: 'TransactionDetailModal',

  computed: {
    transaction() {
      return this.$stores.transactions?.state?.detail?.data;
    },
    loading() {
      return this.$stores.transactions?.state?.detail?.loading;
    },
    canRetryDestination() {
      const srcStatus = this.transaction?.collectStatus;
      return (
        srcStatus === 'success' ||
        srcStatus === 'paid' ||
        srcStatus === 'validated'
      );
    }
  },

  methods: {
    formatNumber(value) {
      if (value === null || value === undefined) return '-';
      return Commons.formatNumber(value);
    },

    formatDate(date) {
      if (!date) return '-';
      return Commons.time(date, 'DD MMM YYYY - HH:mm');
    },

    getStatusText(status) {
      const statusTexts = {
        success: 'Réussi',
        paid: 'Réussi',
        validated: 'Réussi',
        error: 'Échouée',
        failed: 'Échouée',
        created: 'En cours',
        pending: 'En cours',
        unpaid: 'Dépôt en cours',
        refunded: 'Remboursé',
        collected: 'En cours',
        paidFailed: 'En cours',
        funding_failed: 'Échec de dépôt',
        not_subscribed: 'Abonnement échoué',
        transaction_timeout: 'Expirée',
        transaction_failed: 'Échouée',
        failed_amount_missmatch: 'Montant incorrect',
        merchant_balance_is_low: 'Solde insuffisant',
        subscriber_balance_is_low: 'Solde insuffisant',
        not_enough_money: 'Fonds insuffisants',
        min_amount_error: 'Montant minimum non atteint'
      };
      return statusTexts[status] || status || 'Inconnu';
    },

    getStatusClass(status) {
      const statusClasses = {
        success: 'bg-green-100 text-green-700',
        paid: 'bg-green-100 text-green-700',
        validated: 'bg-green-100 text-green-700',
        error: 'bg-red-100 text-red-700',
        failed: 'bg-red-100 text-red-700',
        created: 'bg-yellow-100 text-yellow-700',
        pending: 'bg-yellow-100 text-yellow-700',
        unpaid: 'bg-yellow-100 text-yellow-700',
        collected: 'bg-yellow-100 text-yellow-700',
        paidFailed: 'bg-yellow-100 text-yellow-700',
        funding_failed: 'bg-red-100 text-red-700',
        not_subscribed: 'bg-red-100 text-red-700',
        transaction_timeout: 'bg-red-100 text-red-700',
        transaction_failed: 'bg-red-100 text-red-700',
        failed_amount_missmatch: 'bg-red-100 text-red-700',
        merchant_balance_is_low: 'bg-red-100 text-red-700',
        subscriber_balance_is_low: 'bg-red-100 text-red-700',
        not_enough_money: 'bg-red-100 text-red-700',
        min_amount_error: 'bg-red-100 text-red-700',
        refunded: 'bg-gray-100 text-gray-700'
      };
      return statusClasses[status] || 'bg-gray-100 text-gray-700';
    },

    getStatusDotClass(status) {
      const dotClasses = {
        success: 'bg-green-500',
        paid: 'bg-green-500',
        validated: 'bg-green-500',
        error: 'bg-red-500',
        failed: 'bg-red-500',
        created: 'bg-yellow-500',
        pending: 'bg-yellow-500',
        unpaid: 'bg-yellow-500',
        collected: 'bg-yellow-500',
        paidFailed: 'bg-yellow-500',
        funding_failed: 'bg-red-500',
        not_subscribed: 'bg-red-500',
        transaction_timeout: 'bg-red-500',
        transaction_failed: 'bg-red-500',
        failed_amount_missmatch: 'bg-red-500',
        merchant_balance_is_low: 'bg-red-500',
        subscriber_balance_is_low: 'bg-red-500',
        not_enough_money: 'bg-red-500',
        min_amount_error: 'bg-red-500',
        refunded: 'bg-gray-500'
      };
      return dotClasses[status] || 'bg-gray-500';
    },

    async markAsSuccess() {
      if (!this.transaction?.id) return;

      const result = await window.customPrompt({
        title: 'Marquer comme réussi',
        message: 'Voulez-vous vraiment marquer cette transaction comme réussie ?',
        successText: 'Confirmer',
        cancelText: 'Annuler',
        withInput: false
      });

      if (result?.accepted) {
        this.$bus.emit(this.$constants.EVENTS.TRANSACTIONS.MARK_AS_SUCCESS, {
          transactionId: this.transaction.id
        });
      }
    },

    async markAsRefunded() {
      if (!this.transaction?.id) return;

      const result = await window.customPrompt({
        title: 'Rembourser',
        message: 'Voulez-vous vraiment marquer cette transaction comme remboursée ?',
        successText: 'Confirmer',
        cancelText: 'Annuler',
        withInput: false
      });

      if (result?.accepted) {
        this.$bus.emit(this.$constants.EVENTS.TRANSACTIONS.MARK_AS_REFUNDED, {
          transactionId: this.transaction.id
        });
      }
    },

    async retryDestination() {
      if (!this.transaction?.id) return;

      const result = await window.customPrompt({
        title: 'Relancer la destination',
        message: 'Voulez-vous vraiment relancer la partie destination de cette transaction ?',
        successText: 'Confirmer',
        cancelText: 'Annuler',
        withInput: false
      });

      if (result?.accepted) {
        this.$bus.emit(this.$constants.EVENTS.TRANSACTIONS.RETRY_DESTINATION, {
          transactionId: this.transaction.id
        });
      }
    }
  }
};
</script>
